"use client"

import { motion, useMotionValue, useSpring } from "framer-motion"
import { useEffect, useState } from "react"

export function AnimatedCursor() {
    const [isHovering, setIsHovering] = useState(false)
    const [isVisible, setIsVisible] = useState(false)
    const [isMounted, setIsMounted] = useState(false)

    const cursorX = useMotionValue(-100)
    const cursorY = useMotionValue(-100)

    const springConfig = { damping: 25, stiffness: 300 }
    const cursorXSpring = useSpring(cursorX, springConfig)
    const cursorYSpring = useSpring(cursorY, springConfig)

    useEffect(() => {
        // Only run on client and non-touch devices
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
        if (isTouchDevice) {
            setIsMounted(false)
            return
        }

        setIsMounted(true)

        const moveCursor = (e: MouseEvent) => {
            cursorX.set(e.clientX)
            cursorY.set(e.clientY)
            setIsVisible(true)
        }

        const handleMouseDown = () => setIsHovering(true)
        const handleMouseUp = () => setIsHovering(false)

        const handleElementHover = (e: MouseEvent) => {
            const target = e.target as HTMLElement
            const isInteractive = target.closest('a, button, [role="button"], input, textarea, select, [data-cursor-hover]')
            setIsHovering(!!isInteractive)
        }

        window.addEventListener("mousemove", moveCursor)
        window.addEventListener("mousemove", handleElementHover)
        window.addEventListener("mousedown", handleMouseDown)
        window.addEventListener("mouseup", handleMouseUp)
        document.addEventListener("mouseleave", () => setIsVisible(false))
        document.addEventListener("mouseenter", () => setIsVisible(true))

        return () => {
            window.removeEventListener("mousemove", moveCursor)
            window.removeEventListener("mousemove", handleElementHover)
            window.removeEventListener("mousedown", handleMouseDown)
            window.removeEventListener("mouseup", handleMouseUp)
        }
    }, [cursorX, cursorY])

    // Don't render anything until mounted (prevents hydration mismatch)
    if (!isMounted) {
        return null
    }

    return (
        <>
            {/* Main cursor dot */}
            <motion.div
                className="fixed top-0 left-0 pointer-events-none z-[9999] mix-blend-difference"
                style={{
                    x: cursorXSpring,
                    y: cursorYSpring,
                    translateX: "-50%",
                    translateY: "-50%",
                }}
            >
                <motion.div
                    animate={{
                        scale: isHovering ? 2.5 : 1,
                        opacity: isVisible ? 1 : 0,
                    }}
                    transition={{ type: "spring", damping: 20, stiffness: 300 }}
                    className="w-3 h-3 bg-white rounded-full"
                />
            </motion.div>

            {/* Outer ring */}
            <motion.div
                className="fixed top-0 left-0 pointer-events-none z-[9998]"
                style={{
                    x: cursorXSpring,
                    y: cursorYSpring,
                    translateX: "-50%",
                    translateY: "-50%",
                }}
            >
                <motion.div
                    animate={{
                        scale: isHovering ? 1.5 : 1,
                        opacity: isVisible ? 0.5 : 0,
                    }}
                    transition={{ type: "spring", damping: 20, stiffness: 200 }}
                    className="w-8 h-8 border border-primary rounded-full"
                />
            </motion.div>
        </>
    )
}
